import { useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Loader2, ArrowRight, BarChart2, TrendingUp, Shield, Trophy } from 'lucide-react';
import AnimatedLogo from '@/components/AnimatedLogo';
import { useLanguage } from '@/contexts/LanguageContext';
import { useAuth, RegisterData } from '@/contexts/AuthContext';
import { useMobile } from '@/hooks/use-mobile';
import { useNavigate } from 'react-router-dom';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { motion } from 'framer-motion';
import LanguageSelector from '@/components/LanguageSelector';

const LandingPage = () => {
  const navigate = useNavigate();
  const isMobile = useMobile();
  const { isAuthenticated, login, register, recoverPassword } = useAuth();
  const { t } = useLanguage();
  const [dialogOpen, setDialogOpen] = useState<'login' | 'register' | 'recovery' | null>(null);
  const [loginForm, setLoginForm] = useState({ usernameOrEmail: '', password: '' });
  const [registerForm, setRegisterForm] = useState<RegisterData>({
    username: '',
    email: '',
    preferred_username: '',
    password: '',
    gender: 'male'
  });
  const [recoveryEmail, setRecoveryEmail] = useState('');
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  if (isAuthenticated) {
    navigate('/dashboard');
    return null;
  }
  
  const handleLoginSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await login(loginForm.usernameOrEmail, loginForm.password);
      setDialogOpen(null);
      setLoginForm({ usernameOrEmail: '', password: '' });
    } catch (error) {
      // Error already handled in auth context
    }
  };
  
  const handleRegisterSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await register(registerForm);
      setDialogOpen(null);
      setRegisterForm({
        username: '',
        email: '',
        preferred_username: '',
        password: '',
        gender: 'male'
      });
    } catch (error) {
      // Error already handled in auth context
    } finally {
      setIsSubmitting(false);
    }
  };
  
  const handleRecoverySubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await recoverPassword(recoveryEmail);
      setDialogOpen(null);
      setRecoveryEmail('');
    } catch (error) {
      // Error already handled in auth context
    }
  };

  const renderLoginDialog = () => (
    <Dialog open={dialogOpen === 'login'} onOpenChange={open => !open && setDialogOpen(null)}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>{t('loginDialog.title')}</DialogTitle>
          <DialogDescription>
            {t('loginDialog.description')}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleLoginSubmit} className="grid gap-4 py-4">
          <div className="grid gap-2">
            <Label htmlFor="usernameOrEmail">{t('form.username')}</Label>
            <Input
              id="usernameOrEmail"
              type="text"
              placeholder={t('form.usernamePlaceholder')}
              value={loginForm.usernameOrEmail}
              onChange={(e) => setLoginForm({ ...loginForm, usernameOrEmail: e.target.value })}
              required
            />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="password">{t('form.password')}</Label>
            <Input
              id="password"
              type="password"
              placeholder={t('form.passwordPlaceholder')}
              value={loginForm.password}
              onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
              showPasswordToggle={true}
              required
            />
          </div>
          <Button type="button" variant="link" className="justify-start px-0" onClick={() => setDialogOpen('recovery')}>
            {t('loginDialog.forgotPassword')}
          </Button>
          <Button type="submit">{t('loginDialog.loginButton')}</Button>
          <div className="text-center">
            <span className="text-sm text-muted-foreground">{t('loginDialog.noAccount')}</span>{' '}
            <Button type="button" variant="link" className="p-0" onClick={() => setDialogOpen('register')}>
              {t('loginDialog.registerNow')}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );

  const renderRegisterDialog = () => (
    <Dialog open={dialogOpen === 'register'} onOpenChange={open => !open && setDialogOpen(null)}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>{t('registerDialog.title')}</DialogTitle>
          <DialogDescription>
            {t('registerDialog.description')}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleRegisterSubmit} className="grid gap-4 py-4">
          <div className="grid gap-2">
            <Label htmlFor="username">Nome de usuário</Label>
            <Input
              id="username"
              type="text"
              placeholder="Digite seu nome de usuário para login"
              value={registerForm.username}
              onChange={(e) => setRegisterForm({ ...registerForm, username: e.target.value })}
              required
            />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              placeholder={t('form.emailPlaceholder')}
              value={registerForm.email}
              onChange={(e) => setRegisterForm({ ...registerForm, email: e.target.value })}
              required
            />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="preferred_username">Nome de exibição</Label>
            <Input
              id="preferred_username"
              type="text"
              placeholder="Como você quer ser chamado"
              value={registerForm.preferred_username}
              onChange={(e) => setRegisterForm({ ...registerForm, preferred_username: e.target.value })}
              required
            />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="password">Senha</Label>
            <Input
              id="password"
              type="password"
              placeholder={t('form.passwordPlaceholder')}
              value={registerForm.password}
              onChange={(e) => setRegisterForm({ ...registerForm, password: e.target.value })}
              showPasswordToggle={true}
              required
            />
            <p className="text-xs text-muted-foreground">
              A senha deve ter pelo menos 8 caracteres, incluindo maiúsculas, minúsculas, números e caracteres especiais.
            </p>
          </div>
          <div className="grid gap-2">
            <Label htmlFor="gender">Gênero</Label>
            <Select 
              value={registerForm.gender} 
              onValueChange={(value) => setRegisterForm({ ...registerForm, gender: value as 'male' | 'female' | 'other' })}
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Selecione seu gênero" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="male">Masculino</SelectItem>
                <SelectItem value="female">Feminino</SelectItem>
                <SelectItem value="other">Outro</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Button type="submit" disabled={isSubmitting}>
            {isSubmitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Processando
              </>
            ) : (
              t('registerDialog.registerButton')
            )}
          </Button>
          <div className="text-center">
            <span className="text-sm text-muted-foreground">{t('registerDialog.hasAccount')}</span>{' '}
            <Button type="button" variant="link" className="p-0" onClick={() => setDialogOpen('login')}>
              {t('registerDialog.loginNow')}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );

  const renderRecoveryDialog = () => (
    <Dialog open={dialogOpen === 'recovery'} onOpenChange={open => !open && setDialogOpen(null)}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>{t('recoveryDialog.title')}</DialogTitle>
          <DialogDescription>
            {t('recoveryDialog.description')}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleRecoverySubmit} className="grid gap-4 py-4">
          <div className="grid gap-2">
            <Label htmlFor="recovery-email">Email</Label>
            <Input
              id="recovery-email"
              type="email"
              placeholder={t('form.emailPlaceholder')}
              value={recoveryEmail}
              onChange={(e) => setRecoveryEmail(e.target.value)}
              required
            />
          </div>
          <Button type="submit">{t('recoveryDialog.resetButton')}</Button>
          <Button type="button" variant="outline" onClick={() => setDialogOpen('login')}>
            {t('recoveryDialog.backToLogin')}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );

  return (
    <div className="min-h-screen bg-background">
      {/* Hero Section */}
      <section className="relative overflow-hidden">
        {/* Dynamic background with animated lines */}
        <div className="absolute inset-0 z-0 bg-gradient-to-br from-background to-muted overflow-hidden">
          {[...Array(10)].map((_, i) => (
            <motion.div
              key={i}
              className="absolute bg-primary/10 rounded-full"
              style={{
                width: Math.random() * 300 + 50,
                height: Math.random() * 300 + 50,
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
              }}
              animate={{
                x: [0, Math.random() * 100 - 50],
                y: [0, Math.random() * 100 - 50],
              }}
              transition={{
                duration: Math.random() * 10 + 20,
                repeat: Infinity,
                repeatType: "reverse",
              }}
            />
          ))}
        </div>

        {/* Header with auth buttons */}
        <header className="relative z-10 px-6 py-4 flex justify-between items-center">
          <div className="flex items-center">
            <h1 className="text-2xl font-bold text-primary">Titan Prediction</h1>
          </div>
          
          <div className="flex items-center space-x-3">
            <div className="relative">
              <LanguageSelector />
            </div>
            <Button 
              variant="outline" 
              size="sm" 
              className="h-8 px-3 text-sm flex items-center space-x-1"
              onClick={() => setDialogOpen('login')}
            >
              {t('auth.login')}
            </Button>
            
            <Button 
              variant="default" 
              size="sm" 
              className="h-8 px-3 text-sm flex items-center space-x-1"
              onClick={() => setDialogOpen('register')}
            >
              {t('auth.register')}
            </Button>
          </div>
        </header>

        {/* Hero content */}
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 py-16 md:py-24 lg:py-32">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <motion.h1 
                className="text-4xl sm:text-5xl md:text-6xl font-extrabold leading-tight text-foreground"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
              >
                {t('landing.title')} <span className="text-primary">{t('landing.titleHighlight')}</span> {t('landing.titleEnd')}
              </motion.h1>
              
              <motion.p 
                className="text-lg text-muted-foreground"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.1 }}
              >
                {t('landing.subtitle')}
              </motion.p>
              
              <motion.div 
                className="flex flex-col sm:flex-row gap-4"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.2 }}
              >
                <Button 
                  size="lg" 
                  className="rounded-md font-medium"
                  onClick={() => setDialogOpen('login')}
                >
                  {t('landing.startNow')}
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
                <Button 
                  variant="outline" 
                  size="lg" 
                  className="rounded-md font-medium"
                  onClick={() => setDialogOpen('register')}
                >
                  {t('landing.createFreeAccount')}
                </Button>
              </motion.div>
            </div>
            
            <motion.div
              className="relative"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, delay: 0.3 }}
            >
              {/* Enhanced AI-Powered Analysis Section with animated background */}
              <div className="relative rounded-3xl overflow-hidden shadow-2xl">
                {/* Animated gradient background */}
                <div className="absolute inset-0 bg-gradient-to-br from-primary/20 via-primary/10 to-accent/20">
                  {/* Animated background pattern */}
                  <div className="absolute inset-0">
                    {[...Array(8)].map((_, i) => (
                      <motion.div
                        key={i}
                        className="absolute rounded-full bg-primary/5"
                        style={{
                          width: Math.random() * 200 + 100,
                          height: Math.random() * 200 + 100,
                          left: `${Math.random() * 100}%`,
                          top: `${Math.random() * 100}%`,
                        }}
                        animate={{
                          scale: [1, 1.2, 1],
                          opacity: [0.3, 0.6, 0.3],
                          x: [0, Math.random() * 50 - 25],
                          y: [0, Math.random() * 50 - 25],
                        }}
                        transition={{
                          duration: Math.random() * 8 + 6,
                          repeat: Infinity,
                          repeatType: "reverse",
                          delay: i * 0.5,
                        }}
                      />
                    ))}
                  </div>
                  
                  {/* Diagonal animated lines */}
                  <div className="absolute inset-0 opacity-20">
                    {[...Array(5)].map((_, i) => (
                      <motion.div
                        key={i}
                        className="absolute h-0.5 bg-gradient-to-r from-transparent via-primary to-transparent"
                        style={{
                          width: '120%',
                          top: `${20 + i * 15}%`,
                          left: '-10%',
                          transform: 'rotate(25deg)',
                        }}
                        animate={{
                          opacity: [0, 1, 0],
                          scaleX: [0, 1, 0],
                        }}
                        transition={{
                          duration: 3,
                          repeat: Infinity,
                          delay: i * 0.8,
                        }}
                      />
                    ))}
                  </div>
                </div>

                {/* Glassmorphism card */}
                <motion.div 
                  className="relative z-10 p-8 backdrop-blur-xl bg-white/20 dark:bg-black/20 border border-white/30 dark:border-white/10 rounded-3xl shadow-xl"
                  whileHover={{ 
                    y: -5,
                    transition: { duration: 0.3 }
                  }}
                >
                  <div className="text-center">
                    {/* Animated icon with pulse effect */}
                    <motion.div
                      className="mb-6 flex justify-center"
                      animate={{
                        scale: [1, 1.05, 1],
                      }}
                      transition={{
                        duration: 2,
                        repeat: Infinity,
                        repeatType: "reverse",
                      }}
                    >
                      <div className="relative">
                        <motion.div
                          className="absolute inset-0 rounded-full bg-primary/30"
                          animate={{
                            scale: [1, 1.3, 1],
                            opacity: [0.7, 0, 0.7],
                          }}
                          transition={{
                            duration: 2,
                            repeat: Infinity,
                          }}
                        />
                        <div className="relative z-10 p-4 rounded-full bg-primary/10 backdrop-blur-sm">
                          <BarChart2 className="h-12 w-12 text-primary drop-shadow-lg" />
                        </div>
                      </div>
                    </motion.div>
                    
                    <motion.h3 
                      className="text-2xl font-bold mb-3 text-foreground drop-shadow-sm"
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.5 }}
                    >
                      {t('landing.analysisTitle')}
                    </motion.h3>
                    
                    <motion.p 
                      className="text-muted-foreground font-medium backdrop-blur-sm"
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.7 }}
                    >
                      {t('landing.analysisSubtitle')}
                    </motion.p>
                  </div>
                </motion.div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 bg-muted/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">{t('landing.whyChoose')}</h2>
            <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
              {t('landing.whyChooseDesc')}
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                icon: <TrendingUp className="h-10 w-10 text-primary" />,
                title: t('landing.feature1Title'),
                description: t('landing.feature1Desc')
              },
              {
                icon: <Shield className="h-10 w-10 text-primary" />,
                title: t('landing.feature2Title'),
                description: t('landing.feature2Desc')
              },
              {
                icon: <Trophy className="h-10 w-10 text-primary" />,
                title: t('landing.feature3Title'),
                description: t('landing.feature3Desc')
              }
            ].map((feature, index) => (
              <motion.div 
                key={index}
                className="bg-card rounded-xl p-6 shadow-sm hover:shadow-md transition-all duration-300"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <div className="rounded-full bg-primary/10 p-3 w-fit mb-4">
                  {feature.icon}
                </div>
                <h3 className="text-xl font-bold mb-2">{feature.title}</h3>
                <p className="text-muted-foreground">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-primary/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 text-center">
          <h2 className="text-3xl font-bold mb-4">{t('landing.readyTitle')}</h2>
          <p className="text-lg text-muted-foreground max-w-3xl mx-auto mb-8">
            {t('landing.readyDesc')}
          </p>
          <Button 
            size="lg" 
            className="rounded-md font-medium"
            onClick={() => setDialogOpen('register')}
          >
            {t('landing.createFreeAccount')}
          </Button>
        </div>
      </section>

      {/* Footer */}
      <section className="bg-muted py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div>
              <h3 className="text-lg font-bold mb-4">Titan Prediction</h3>
              <p className="text-muted-foreground">
                {t('landing.footerDesc')}
              </p>
            </div>
            
            <div>
              <h4 className="font-medium mb-4">{t('landing.resources')}</h4>
              <ul className="space-y-2">
                <li><a href="#" className="text-muted-foreground hover:text-primary">{t('landing.analysis')}</a></li>
                <li><a href="#" className="text-muted-foreground hover:text-primary">{t('landing.predictions')}</a></li>
                <li><a href="#" className="text-muted-foreground hover:text-primary">{t('landing.statistics')}</a></li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-medium mb-4">{t('landing.company')}</h4>
              <ul className="space-y-2">
                <li><a href="#" className="text-muted-foreground hover:text-primary">{t('landing.aboutUs')}</a></li>
                <li><a href="#" className="text-muted-foreground hover:text-primary">{t('landing.blog')}</a></li>
                <li><a href="#" className="text-muted-foreground hover:text-primary">{t('landing.contact')}</a></li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-medium mb-4">{t('landing.legal')}</h4>
              <ul className="space-y-2">
                <li><a href="#" className="text-muted-foreground hover:text-primary">{t('landing.terms')}</a></li>
                <li><a href="#" className="text-muted-foreground hover:text-primary">{t('landing.privacy')}</a></li>
                <li><a href="#" className="text-muted-foreground hover:text-primary">{t('landing.responsibility')}</a></li>
              </ul>
            </div>
          </div>
          
          <div className="border-t border-border mt-8 pt-8 text-center text-muted-foreground">
            <p>{t('landing.copyright', { year: new Date().getFullYear() })}</p>
          </div>
        </div>
      </section>

      {renderLoginDialog()}
      {renderRegisterDialog()}
      {renderRecoveryDialog()}
    </div>
  );
};

export default LandingPage;
